import React, { useState } from "react";
import {
    Box,
    Typography,
    Divider,
    Checkbox,
    Button,
    CircularProgress,
    IconButton,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { FileUpload } from "./FileUpload";
import { AttachFile, Edit } from "@mui/icons-material";
import { formatDate } from "../utils";
import { useCapabilities } from "../../hooks/useCapabilities";

interface CheckListProps {
    completedStatus: "completed" | "in_progress" | "not_started";
    title: string;
    owner: string;
    completedDate: string;
    downloadLink: string;
    isRequired: boolean;
    checkListId: string;
    isEvidenceRequired: boolean;
    onSave: () => Promise<void> | void;
    isLoading: boolean;
    onUploadComplete: (context: { isEdit: boolean; markComplete: boolean }) => void;
    fileUploadRef?: React.RefObject<any>;
    checklistType?: "activity" | "stageGate";
    allowAttachmentComplete?: boolean;
    selectedChecklistId: string | null;
    setSelectedChecklistId: React.Dispatch<
    React.SetStateAction<string | null>
  >;
    selectedEditId: string | null;
    setSelectedEditId: React.Dispatch<
        React.SetStateAction<string | null>
    >;
}

export const CheckList: React.FC<CheckListProps> = ({
    completedStatus,
    completedDate,
    title,
    owner,
    checkListId,
    isRequired,
    isEvidenceRequired,
    onSave,
    isLoading = false,
    onUploadComplete,
    fileUploadRef,
    checklistType,
    allowAttachmentComplete = false,
    selectedChecklistId,
    setSelectedChecklistId,
    selectedEditId,
    setSelectedEditId,
}) => {

    const { isAllowedToCompleteChecklist } = useCapabilities();

    const isCompleted = completedStatus === "completed";
    const status = isCompleted || selectedChecklistId === checkListId;
    const editStatus = selectedEditId === checkListId;
    const [error, setError] = useState<string>("");

    // const [editStatus, setEditStatus] = useState(false);

    const handleSave = async () => {
        await onSave();
        setSelectedEditId(null);
    };

    const handleCheckboxChange = () => {
        setSelectedEditId(checkListId);
        setSelectedChecklistId((prev) =>
            prev === checkListId ? null : checkListId
        );
    };

    const canOpenAttachmentEditor = isCompleted || allowAttachmentComplete;
    const canEditAttachments = editStatus || (status && !isCompleted) ;
    const showActionButtons =
        (status && (!isCompleted )) ||
        (editStatus && (isCompleted || allowAttachmentComplete || status));
 

    return (
        <Box
            borderColor={"divider"}
            borderRadius={1}
            px={1}
            py={2}
            sx={{ borderWidth: "1px", borderStyle: "solid" }}
            mb={2}
        >
            <Box display="flex" alignItems="start" width="100%" gap={1}>

                {/* checkbox */}
                {isAllowedToCompleteChecklist && (
                    <Checkbox
                        checked={status}
                        onChange={handleCheckboxChange}
                        disabled={(isCompleted || isLoading) && !editStatus}
                    />
                )}

                {/* Title + owner */}
                <Box flex={1} display="flex" flexDirection="column" gap={0.5}>
                    <Typography variant="body2">{title}</Typography>

                    <Typography variant="caption" color="text.secondary">
                        {isCompleted
                            ? `Completed by ${owner} on ${formatDate(completedDate)}`
                            : `Owner: ${owner}`}
                    </Typography>

                    {/* File upload */}
                    { (
                        <>
                            <FileUpload
                                type="checklist"
                                ref={fileUploadRef}
                                typeId={checkListId}
                                isRequired={isEvidenceRequired}
                                isDisableEdit={!canEditAttachments}
                                isDisableDelete={!canEditAttachments}
                                onUploadComplete={() => onUploadComplete({ isEdit: editStatus, markComplete: status })}
                            />
                            {error && (
                                <Typography variant="caption" color="error">
                                    {error}
                                </Typography>
                            )}
                        </>
                    )}
                </Box>

                {/* Attachment action */}
               {isAllowedToCompleteChecklist && canOpenAttachmentEditor && (
                    <IconButton
                        sx={{ color: "primary.main" }}
                        onClick={() => {
                            setSelectedChecklistId(null);
                            setSelectedEditId((prev) =>
                                prev === checkListId ? null : checkListId
                            );
                        }}
                    >
                        {checklistType === "stageGate" ? (
                            <Edit />
                        ) : (
                            <AttachFile fontSize="small" />
                        )}
                    </IconButton>
                )}
            </Box>

    
      {/* Save / Update button */}
            {showActionButtons && (
                <Box display="flex" flexDirection="column" width="100%" gap={1}>
                    <Divider sx={{ my: 1 }} />
                    <Box display="flex" justifyContent="flex-end" width="100%" gap={2}>
                        <Button onClick={() => {
                            if (fileUploadRef && fileUploadRef.current && fileUploadRef.current.clearFiles) {
                                fileUploadRef.current.clearFiles();
                            }
                         setSelectedEditId(null);
                            setSelectedChecklistId(null);
                        }}> Cancel</Button>
                        <Button
                            startIcon={
                                isLoading ? (
                                    <CircularProgress size={18} color="inherit" />
                                ) : (
                                    <CheckCircleOutlineIcon />
                                )
                            }
                            variant="contained"
                            onClick={handleSave}
                            disabled={isLoading}
                        >
                            {isLoading
                                ? "Saving..."
                                :  editStatus && !status
                                    ? "Save attachments"
                                : editStatus && status
                                    ? "Save details"
                                    : "Update details"}
                        </Button>
                    </Box>
                </Box>
            )}

        </Box>
    );
};
